/**
 * MenuShowcase — fw-choi UI 컴포넌트 쇼케이스(데모). 실제 업무 화면 아님.
 * 【역할】 공통 컴포넌트/패턴의 사용 예시를 한 화면에 모아 보여준다.
 */
import { Card, Menu, Breadcrumb, Steps, Tabs, Anchor, Dropdown, Space, Typography } from 'antd'
import {
  MailOutlined,
  AppstoreOutlined,
  SettingOutlined,
  HomeOutlined,
  DownOutlined,
  UserOutlined
} from '@ant-design/icons'
import PageContainer from '@/components/common/PageContainer'
import { useLabel } from '@/framework/useLabel'

const { Title } = Typography

const items = [
  { key: 'mail', icon: <MailOutlined />, label: 'Mail' },
  { key: 'app', icon: <AppstoreOutlined />, label: 'App' },
  {
    key: 'sub',
    icon: <SettingOutlined />,
    label: 'Settings',
    children: [
      { key: 's1', label: 'General' },
      { key: 's2', label: 'Profile' },
      { key: 's3', label: 'Notification' }
    ]
  }
]

/**
 * ============================================================================
 * MenuShowcase — antd 네비게이션 계열 카탈로그
 * ============================================================================
 *
 * 【역할】 메뉴/경로/단계/탭/앵커/드롭다운 등 사이트 내 이동 UI variant 를
 *         한 화면에 그리드로 나열.
 *
 * 【시연 항목】
 *   - Inline Menu     — `mode='inline'` + 서브 그룹
 *   - Horizontal Menu — `mode='horizontal'`
 *   - Breadcrumb + Dropdown — 메뉴 항목과 결합한 빵부스러기
 *   - Steps           — default + navigation 타입
 *   - Tabs            — type='card' 기본 탭
 *   - Anchor          — direction='horizontal'
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/menu' }` (lazy)
 * 【포팅 원본】 자체 작성 (Wave 16 컴포넌트 카탈로그)
 * 【의존성】 antd Menu/Breadcrumb/Steps/Tabs/Anchor/Dropdown/Space/Typography,
 *           @ant-design/icons
 * 【참고】 https://ant.design/components/menu
 */
const dropdownMenu = {
  items: [
    { key: '1', label: 'Option 1' },
    { key: '2', label: 'Option 2' },
    { key: '3', label: 'Option 3' }
  ]
}

/**
 * MenuShowcase — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function MenuShowcase() {
  const { t } = useLabel()
  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_MENU_COMPONENTS', 'Menu/Navigation 컴포넌트')}
        </Title>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gridTemplateAreas: `
            "inline horizontal breadcrumb"
            "steps tabs anchor"
          `,
          gap: 12
        }}
      >
        <Card
          size="small"
          title="Inline Menu"
          styles={{ body: { padding: 4, height: 'calc(100% - 40px)', overflow: 'auto' } }}
          style={{ gridArea: 'inline', display: 'flex', flexDirection: 'column' }}
        >
          <Menu mode="inline" items={items} defaultOpenKeys={['sub']} style={{ border: 'none' }} />
        </Card>

        <Card
          size="small"
          title="Horizontal Menu"
          styles={{ body: { padding: 8 } }}
          style={{ gridArea: 'horizontal' }}
        >
          <Menu mode="horizontal" items={items} />
        </Card>

        <Card
          size="small"
          title="Breadcrumb / Dropdown"
          styles={{ body: { padding: 12 } }}
          style={{ gridArea: 'breadcrumb' }}
        >
          <Breadcrumb
            items={[
              {
                href: '#',
                title: (
                  <>
                    <HomeOutlined /> Home
                  </>
                )
              },
              {
                href: '#',
                title: (
                  <>
                    <UserOutlined /> {t('LBL_USER', '사용자')}
                  </>
                ),
                menu: {
                  items: [
                    { key: '1', label: t('LBL_LIST', '목록') },
                    { key: '2', label: t('LBL_DETAIL', '상세') },
                    { key: '3', label: t('LBL_NEW', '신규') }
                  ]
                }
              },
              { title: 'Application' }
            ]}
          />
          <div style={{ marginTop: 10 }}>
            <Dropdown menu={dropdownMenu}>
              <a onClick={(e) => e.preventDefault()}>
                <Space>
                  Dropdown menu link
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          </div>
        </Card>

        <Card
          size="small"
          title="Steps"
          styles={{ body: { padding: 12, overflow: 'auto' } }}
          style={{ gridArea: 'steps', display: 'flex', flexDirection: 'column' }}
        >
          <Steps
            size="small"
            current={1}
            status="error"
            items={[
              { title: t('LBL_DONE', '완료') },
              { title: t('LBL_IN_PROGRESS', '진행 중') },
              { title: t('LBL_WAITING', '대기') }
            ]}
          />
          <div style={{ height: 12 }} />
          <Steps
            size="small"
            type="navigation"
            current={2}
            items={[
              { title: `${t('LBL_STEP', '단계')} 1`, status: 'finish' },
              { title: `${t('LBL_STEP', '단계')} 2`, status: 'finish' },
              { title: `${t('LBL_STEP', '단계')} 3`, status: 'process' }
            ]}
          />
        </Card>

        <Card
          size="small"
          title="Tabs"
          styles={{ body: { padding: 8 } }}
          style={{ gridArea: 'tabs' }}
        >
          <Tabs
            size="small"
            type="card"
            items={[
              { key: '1', label: 'Tab 1', children: 'Content 1' },
              { key: '2', label: 'Tab 2', children: 'Content 2' },
              { key: '3', label: 'Tab 3', children: 'Content 3' }
            ]}
          />
        </Card>

        <Card
          size="small"
          title="Anchor"
          styles={{ body: { padding: 12 } }}
          style={{ gridArea: 'anchor' }}
        >
          <Anchor
            direction="horizontal"
            affix={false}
            items={[
              { key: 'sec1', href: '#sec1', title: `${t('LBL_SECTION', '섹션')} 1` },
              { key: 'sec2', href: '#sec2', title: `${t('LBL_SECTION', '섹션')} 2` },
              { key: 'sec3', href: '#sec3', title: `${t('LBL_SECTION', '섹션')} 3` }
            ]}
          />
        </Card>
      </div>
    </PageContainer>
  )
}
